import { useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Barcode } from "./Barcode";
import { inr, dt } from "@/lib/api";
import { useApp } from "@/lib/app-context";

const PAGE = { a4: "A4", "80mm": "80mm auto", "58mm": "58mm auto" };
const WIDTH = { a4: "190mm", "80mm": "72mm", "58mm": "48mm" };

export function doPrint(format) {
  const s = document.createElement("style");
  s.innerHTML = `@page { size: ${PAGE[format]}; margin: ${format === "a4" ? "10mm" : "2mm"}; }`;
  document.head.appendChild(s);
  setTimeout(() => {
    window.print();
    s.remove();
  }, 50);
}

const Totals = ({ t, doc }) => (
  <table>
    <tbody>
      <tr><td>Subtotal</td><td style={{ textAlign: "right" }}>{inr(t.subtotal)}</td></tr>
      {t.discount > 0 && <tr><td>Discount</td><td style={{ textAlign: "right" }}>-{inr(t.discount)}</td></tr>}
      <tr><td>Taxable value</td><td style={{ textAlign: "right" }}>{inr(t.taxable)}</td></tr>
      <tr><td>CGST</td><td style={{ textAlign: "right" }}>{inr(t.cgst)}</td></tr>
      <tr><td>SGST</td><td style={{ textAlign: "right" }}>{inr(t.sgst)}</td></tr>
      {t.round_off !== 0 && <tr><td>Round off</td><td style={{ textAlign: "right" }}>{inr(t.round_off)}</td></tr>}
      <tr style={{ fontWeight: 700, fontSize: "1.15em" }}><td>GRAND TOTAL</td><td style={{ textAlign: "right" }}>{inr(t.grand_total)}</td></tr>
      {(doc.payments || []).map((p, i) => <tr key={i}><td>Paid · {p.method.toUpperCase()}{p.reference ? ` (${p.reference})` : ""}</td><td style={{ textAlign: "right" }}>{inr(p.amount)}</td></tr>)}
      {doc.change > 0 && <tr><td>Change</td><td style={{ textAlign: "right" }}>{inr(doc.change)}</td></tr>}
    </tbody>
  </table>
);

const Header = ({ s, doc }) => (
  <div style={{ textAlign: "center" }}>
    <div style={{ fontWeight: 800, fontSize: "1.3em" }}>{s.business_name}</div>
    {doc.branch?.name && <div>{doc.branch.name}</div>}
    {(doc.branch?.address || s.address) && <div>{doc.branch?.address || s.address}</div>}
    {(doc.branch?.phone || s.phone) && <div>Ph: {doc.branch?.phone || s.phone}</div>}
    {(doc.branch?.gstin || s.gstin) && <div>GSTIN: {doc.branch?.gstin || s.gstin}</div>}
  </div>
);

export const InvoiceDoc = ({ doc, format, s }) => {
  const title = doc.channel === "online" || doc.number?.startsWith("ORD") ? "ORDER INVOICE" : "TAX INVOICE";
  if (format === "a4")
    return (
      <div className="a4" style={{ width: WIDTH.a4, padding: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div><div style={{ fontWeight: 800, fontSize: 20 }}>{s.business_name}</div><div>{doc.branch?.name}</div><div>{s.address}</div>
            <div>{s.gstin && `GSTIN: ${s.gstin}`}</div></div>
          <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div><div>No: <b>{doc.number}</b></div>
            <div>Date: {dt(doc.created_at)}</div>{doc.revision > 0 && <div>Revised (rev {doc.revision})</div>}</div>
        </div>
        <div style={{ margin: "10px 0" }}>Bill to: <b>{doc.customer?.name}</b> {doc.customer?.mobile}</div>
        <table>
          <thead><tr><th>#</th><th>Item</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Disc</th><th>GST%</th><th>Taxable</th><th>Tax</th><th>Amount</th></tr></thead>
          <tbody>{doc.lines.map((l, i) => (
            <tr key={i}><td>{i + 1}</td><td>{l.name}</td><td>{l.hsn}</td><td>{l.qty} {l.unit}</td><td>{inr(l.price)}</td><td>{inr(l.discount)}</td>
              <td>{l.tax_rate}</td><td>{inr(l.taxable)}</td><td>{inr(l.tax)}</td><td>{inr(l.total)}</td></tr>))}</tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
          <div><QRCodeSVG value={doc.number} size={72} /><div style={{ marginTop: 6 }}>Payment: {String(doc.payment_type).toUpperCase()}</div></div>
          <div style={{ width: 280 }}><Totals t={doc.totals} doc={doc} /></div>
        </div>
        <p style={{ marginTop: 16, textAlign: "center" }}>{s.invoice_footer}</p>
      </div>
    );
  return (
    <div className="receipt" style={{ width: WIDTH[format], fontSize: format === "58mm" ? 9.5 : 11 }}>
      <Header s={s} doc={doc} />
      <div className="dash" />
      <div style={{ textAlign: "center", fontWeight: 700 }}>{title}</div>
      <div>No: {doc.number}</div><div>Date: {dt(doc.created_at)}</div>
      <div>Customer: {doc.customer?.name} {doc.customer?.mobile}</div>
      {doc.cashier?.name && <div>Cashier: {doc.cashier.name}</div>}
      <div className="dash" />
      <table><tbody>{doc.lines.map((l, i) => (
        <tr key={i}><td>{l.name}<br />{l.qty} {l.unit} × {inr(l.price)}{l.discount > 0 ? ` -${inr(l.discount)}` : ""} · GST {l.tax_rate}%</td>
          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{inr(l.total)}</td></tr>))}</tbody></table>
      <div className="dash" />
      <Totals t={doc.totals} doc={doc} />
      <div className="dash" />
      <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}><Barcode value={doc.number} height={28} width={1} fontSize={9} /></div>
      <p style={{ textAlign: "center", marginTop: 4 }}>{s.invoice_footer}</p>
    </div>
  );
};

export const PickingDoc = ({ doc, format }) => (
  <div className={format === "a4" ? "a4" : "receipt"} style={{ width: WIDTH[format], padding: format === "a4" ? 8 : 0 }}>
    <div style={{ textAlign: "center", fontWeight: 800, fontSize: "1.2em" }}>PICKING TICKET / KOT</div>
    <div>Order: <b>{doc.number}</b></div>
    <div>Channel: {(doc.channel || "pos").toUpperCase()}</div>
    <div>Date: {dt(doc.created_at)}</div>
    {doc.customer && <div>Customer: {doc.customer.name} {doc.customer.mobile}</div>}
    <div>Status: {doc.status} · Picking: {doc.pick_status || "n/a"}</div>
    <div className="dash" style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
    <table><thead><tr><th style={{ textAlign: "left" }}>Item / Rack / Batch</th><th style={{ textAlign: "right" }}>Qty</th><th>✓</th></tr></thead>
      <tbody>{doc.lines.map((l, i) => (
        <tr key={i}><td><b>{l.name}</b> <span>({l.erp_code})</span>
          {(l.allocations || []).map((a, j) => <div key={j}>→ RACK {a.location_code || "UNASSIGNED"} · Batch {a.batch_no || "-"}{a.expiry_date ? ` · Exp ${a.expiry_date}` : ""} · {a.qty}</div>)}</td>
          <td style={{ textAlign: "right", fontWeight: 700 }}>{l.qty} {l.unit}</td><td style={{ textAlign: "center" }}>{l.picked ? "✔" : "☐"}</td></tr>))}</tbody></table>
    {doc.instructions && <><div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} /><div><b>Instructions:</b> {doc.instructions}</div></>}
    <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
    <div>Picked by: __________ Packed by: __________</div>
  </div>
);

export function PrintDialog({ open, onClose, doc, kinds = ["invoice", "kot"] }) {
  const { settings } = useApp();
  const [format, setFormat] = useState(settings?.default_print_format || "80mm");
  const [kind, setKind] = useState(kinds[0]);
  if (!doc || !settings) return null;
  const content = kind === "invoice" ? <InvoiceDoc doc={doc} format={format} s={settings} /> : <PickingDoc doc={doc} format={format} />;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="print-dialog">
        <DialogHeader><DialogTitle>Print {doc.number}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {kinds.map((k) => <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)} data-testid={`print-kind-${k}`}>{k === "invoice" ? "Invoice" : "KOT / Picking"}</Button>)}
          <span className="w-px bg-border mx-1" />
          {["a4", "80mm", "58mm"].map((f) => <Button key={f} size="sm" variant={format === f ? "secondary" : "outline"} onClick={() => setFormat(f)} data-testid={`print-format-${f}`}>{f.toUpperCase()}</Button>)}
          <Button size="sm" className="ml-auto" onClick={() => doPrint(format)} data-testid="print-now-button"><Printer className="h-4 w-4 mr-1" />Print</Button>
        </div>
        <div className="bg-slate-100 rounded-lg p-4 flex justify-center overflow-x-auto"><div className="bg-white shadow p-2" data-testid="print-preview">{content}</div></div>
        {open && createPortal(<div className="print-area">{content}</div>, document.body)}
      </DialogContent>
    </Dialog>
  );
}
